import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { logEvent } from 'firebase/analytics';
import { useFirebase } from '../contexts/FirebaseContext';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to Firebase Analytics
    this.logErrorToFirebase(error, errorInfo);
  }

  logErrorToFirebase = (error, errorInfo) => {
    const { analytics } = this.props;
    const { currentUser } = this.props;

    if (analytics) {
      try {
        // Log error event to Firebase Analytics
        logEvent(analytics, 'client_error', {
          error_type: 'react_error',
          error_message: error?.message?.substring(0, 100) || 'Unknown error',
          error_name: error?.name || 'UnknownError',
          component_stack: errorInfo?.componentStack?.substring(0, 500) || 'No component stack',
          user_id: currentUser?.uid || 'anonymous',
          timestamp: new Date().toISOString(),
          page_url: window.location.pathname
        });
      } catch (logError) {
        console.error('Failed to log error to Firebase:', logError);
      }
    }

    // Also log to console for development
    console.error('Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            padding: 3,
            textAlign: 'center'
          }}
        >
          <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We've logged this error and will look into it. Please try reloading the page or return to the dashboard.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReload}
            >
              Reload Page
            </Button>
            <Button
              variant="outlined"
              onClick={this.handleGoHome}
            >
              Go to Dashboard
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 4, maxWidth: 800, textAlign: 'left' }}>
              <Typography variant="h6" color="error" gutterBottom>
                Development Error Details:
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f5f5f5', 
                padding: 2, 
                borderRadius: 1, 
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}>
                <Typography variant="body2" component="pre">
                  {this.state.error && this.state.error.toString()}
                </Typography>
                <Typography variant="body2" component="pre" sx={{ mt: 2 }}>
                  {this.state.errorInfo.componentStack}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

// HOC wrapper to inject Firebase and Auth context
function ErrorBoundaryWrapper({ children }) {
  const { analytics } = useFirebase();
  const { currentUser } = useAuth();

  return (
    <ErrorBoundary analytics={analytics} currentUser={currentUser}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundaryWrapper;